import { dateOnly } from "./leaseAreaBasis.js";

// Validate the entire read, not just the selected year. A broken read must never
// become "no monthly evidence" and silently enable annual/12.
export function monthlyPayableEvidence(lease, nativeCurrency = lease?.currency) {
  const rows = lease?.monthlyCosts;
  if (lease?.monthlyCostsStatus && lease.monthlyCostsStatus !== "loaded") {
    return { state: "invalid", issue: "Monthly payable evidence could not be loaded", years: new Map() };
  }
  if (rows === undefined) return lease?.monthlyCostsStatus
    ? { state: "invalid", issue: "Monthly payable rows missing from loaded response", years: new Map() }
    : { state: "empty", years: new Map() }; // older payloads
  if (!Array.isArray(rows)) return { state: "invalid", issue: "Malformed monthly payable evidence", years: new Map() };
  const years = new Map();
  const seen = new Set();
  for (const row of rows) {
    const month = row?.month instanceof Date && !Number.isFinite(row.month.getTime()) ? null : dateOnly(row?.month);
    const rent = row?.rentLocal;
    const total = row?.totalLocal;
    const number = (v) => (typeof v === "number" || (typeof v === "string" && v.trim() !== "")) && Number.isFinite(Number(v));
    if (!/^\d{4}-(0[1-9]|1[0-2])-01$/.test(month || "") || seen.has(month) ||
        !number(rent) || !number(total) || !row.currency || row.currency !== nativeCurrency ||
        !row.source?.trim() || (row.leaseId && lease?.id && row.leaseId !== lease.id)) {
      return { state: "invalid", issue: "Invalid monthly payable amount, identity or currency", years: new Map() };
    }
    seen.add(month);
    const year = Number(month.slice(0, 4));
    const evidence = years.get(year) || { year, currency: row.currency, rows: [], net: 0 };
    evidence.rows.push({ ...row, month });
    evidence.net += Number(rent);
    years.set(year, evidence);
  }
  // Source amounts have cents; round once after summing, never round installments.
  for (const year of years.values()) year.net = Math.round((year.net + Number.EPSILON) * 100) / 100;
  return { state: rows.length ? "actual" : "empty", years };
}

export function actualPayableMonth(evidence, year, month, currency) {
  if (evidence.state === "invalid") return { amount: null, currency, year, month,
    basis: "unresolved", approximate: false, issue: evidence.issue };
  const annual = evidence.years.get(Number(year));
  if (!annual) return { amount: null, currency, year, month,
    basis: "not_in_source", approximate: false };
  const observation = annual.rows.find((row) => Number(row.month.slice(5, 7)) === Number(month));
  const amounts = Array.from({ length: 12 }, (_, index) =>
    Number(annual.rows.find((row) => Number(row.month.slice(5, 7)) === index + 1)?.rentLocal ?? 0));
  return { amount: observation ? Number(observation.rentLocal) : 0,
    currency: annual.currency, year, month, basis: "monthly_actual", approximate: false,
    sourced: Boolean(observation), displayOnlyZero: !observation,
    source: observation?.source ?? null, variable: new Set(amounts).size > 1 };
}

// Overlay only the payable columns; recorded contractual anchors stay untouched.
// Both server and client call this after their existing fallback derivations.
export function applyMonthlyPayableYears(schedule, lease) {
  const evidence = monthlyPayableEvidence(lease);
  if (evidence.state === "empty") return schedule;
  if (evidence.state === "invalid") return schedule.map((row) => ({
    ...row, net: null, abatement: null, basis: "unresolved", issue: evidence.issue,
  }));
  const rows = new Map(schedule.map((row) => [Number(row.year), { ...row }]));
  for (const actual of evidence.years.values()) {
    const existing = rows.get(actual.year);
    const row = existing || { year: actual.year, contractual: null, ratePsf: null, escPct: null };
    const hasAnchor = row.contractual != null && Number.isFinite(Number(row.contractual)) &&
      !row.isFallback && !row.isProjected && !row.isNotInSource;
    const anchorCurrency = row.contractualCurrency || row.currency;
    const comparable = hasAnchor && anchorCurrency === actual.currency;
    rows.set(actual.year, { ...row, currency: actual.currency,
      // Payable evidence describes the complete source year, not a term stub.
      months: 12, isPartial: false, partialFrom: null, partialTo: null,
      contractual: hasAnchor ? row.contractual : null, contractualCurrency: hasAnchor ? anchorCurrency : actual.currency,
      recordedContractual: row.contractual == null ? null : { amount: row.contractual, currency: anchorCurrency },
      recordedAbatement: row.recordedAbatement ?? row.abatement ?? null,
      net: actual.net, abatement: comparable
        ? Math.max(0, Math.round((Number(row.contractual) - actual.net) * 100) / 100) : null,
      abatementSource: comparable ? "monthly_actual" : null,
      basis: "monthly_actual", approximate: false, isNotInSource: false, isFallback: false,
      sourceStatus: "monthly_actual",
    });
  }
  return [...rows.values()].sort((a, b) => Number(a.year) - Number(b.year));
}
